import {Injectable} from "@angular/core";
import {CssWord} from "../../content/content-view.objects";
import {INTERVENTIONTYPE} from "../../constants";

export interface IGrammar {
    numStates: number;
    start: number;
    end: number;
    transitions:Array<ITransition>;
}

export interface ITransition {
    from: number;
    to: number;
    logp?: number;
    word: string;
    glue?: boolean;
}

interface ISliderSettings {
    pronunciationCorrectness0: number;
    pronunciationCorrectness50: number;
    pronunciationCorrectness100: number;
}

export interface IProbability {
    silenceSelfLoop: number;
    fillerSelfLoop: number;
    useFillerSelfLoop: boolean;
    silenceSkip: ISliderSettings;
    lastWordSkip: ISliderSettings;
    glueSkip: ISliderSettings;
    foilSkip: ISliderSettings;
    startSkip: ISliderSettings;
    silenceBack: number;
    reset: number;
}

@Injectable()
export class RecognizerGrammarService {
    public static readonly START_TAG: string = "_START";
    public static readonly FOIL_TAG: string = "_FOIL";
    public static readonly SILENCE: string = "<S>";
    public static readonly FILLER: string = "CIFILLER-C";
    public static readonly WCPM_THRESHOLD = 120;
    public static readonly DEFAULT_PRONUNCIATION_CORRECTNESS= 50;
    public static readonly ADJUSTED_PRONUNCIATION_CORRECTNESS= 30;
    public static readonly MAX_PRONUNCIATION_CORRECTNESS= 100;
    private grammar: IGrammar;
    public probability: IProbability = {
        silenceSelfLoop: -5000,
        fillerSelfLoop: -500000,
        useFillerSelfLoop: true,
        silenceSkip: {
            pronunciationCorrectness0: -1500000,
            pronunciationCorrectness50: -100000,
            pronunciationCorrectness100: -15000},
        lastWordSkip: {
            pronunciationCorrectness0: -1500000,
            pronunciationCorrectness50: -150000,
            pronunciationCorrectness100: -25000},
        glueSkip: {
            pronunciationCorrectness0: -150000,
            pronunciationCorrectness50: -50000,
            pronunciationCorrectness100: -5000},
        foilSkip: {
            pronunciationCorrectness0: -1500000,
            pronunciationCorrectness50: -320000,
            pronunciationCorrectness100: -120000},
        startSkip: {
            pronunciationCorrectness0: -500000,
            pronunciationCorrectness50: -150000,
            pronunciationCorrectness100: -50000},
        silenceBack: 30000,
        reset: -10000
    };

    public create(words: CssWord[], dictionaryList: string[], pronunciationCorrectness: number,
                  interventionType: INTERVENTIONTYPE): IGrammar {

        // base grammar is assumed to be an RA sentence, in order, one transition per word
        // Terminology:
        // Filler: general model for speech
        // Glue: short common word such as the or a, treated differently in the grammar - assumed not important
        // Foil: partial pronunciation of a word
        // pronunciationCorrectness: MSL setting which determines how strict or lenient we are on user's reading.

        this.createGrammar(words, interventionType, pronunciationCorrectness, dictionaryList);
        // additions to the base grammar model disfluency, errors, and extraneous speech
        // allow a word to be substituted with silence or noise
        this.addSkips(pronunciationCorrectness);

        // backwards transitions
        this.addBackTransitions(pronunciationCorrectness);

        // TODO: back to start (first or intervened word) transitions
        if (interventionType !== INTERVENTIONTYPE.no_intervention) {
            this.addResetTransitions(pronunciationCorrectness);
        }

        // self-loops
        this.addSelfTransitions(pronunciationCorrectness);

        let wordString: string = "_TEST_AUTOMATION_ grammar detail" +
                                 ((interventionType === INTERVENTIONTYPE.audio_intervention) ? "_AUDIO_:" : ":");
        this.grammar.transitions.forEach((transition) => {
            wordString += "from:" + transition.from + " to:" + transition.to
                          + ":" + transition.word + ":" + transition.logp;
        });
        this.logEntry(wordString);
        return this.grammar;

    }

    public adjustPronunciationCorrectness(pronunciationCorrectness: number, wcpm: string): number {
        let adjustedValue = pronunciationCorrectness;
        let wcpmValue = Number(wcpm);
        if ((wcpmValue !== Number.NaN) && (wcpmValue >= RecognizerGrammarService.WCPM_THRESHOLD)) {
            let originalDefault = RecognizerGrammarService.DEFAULT_PRONUNCIATION_CORRECTNESS;
            let adjustedDefault = RecognizerGrammarService.ADJUSTED_PRONUNCIATION_CORRECTNESS;
            if (pronunciationCorrectness <= originalDefault) {
                adjustedValue = pronunciationCorrectness * (adjustedDefault / originalDefault);
            } else {
                let max = RecognizerGrammarService.MAX_PRONUNCIATION_CORRECTNESS; // rhs of slider
                let originalToMax = max - originalDefault;
                let adjustedToMax = max - adjustedDefault;
                let delta = pronunciationCorrectness - originalDefault;
                adjustedValue = adjustedDefault + delta * adjustedToMax / originalToMax;
            }
        }
        return adjustedValue;
    }

    private createGrammar(words: CssWord[], interventionType: INTERVENTIONTYPE, pronunciationCorrectness: number,
                          dictionaryList: string[]): void {
        // transitions: add base sequence
        let transitions: Array<ITransition> = [];
        let transition: ITransition;
        let additionalStateIndex: number = words.length;
        for (let i = 0; i < words.length; i++) {
            let notRequired: boolean = !words[i].isRequired() ||
                                       ((interventionType === INTERVENTIONTYPE.audio_intervention) && (i === 0));
            if (!words[i].dictionary) {
                transition = {from: i, to: i + 1, word: words[i].word.toUpperCase(), logp: 0.0, glue: notRequired};
                transitions = transitions.concat(
                    this.transitionAndFoils(transition, pronunciationCorrectness, dictionaryList));
            } else {
                words[i].dictionary.forEach((phrase) => {
                    let phraseWords: string[] = phrase.split(" ");
                    if (phraseWords.length === 1) {
                        transition = {
                            from: i, to: i + 1,
                            word: phraseWords[0].toUpperCase(), logp: 0.0, glue: notRequired
                        };
                        transitions = transitions.concat(
                            this.transitionAndFoils(transition, pronunciationCorrectness, dictionaryList));
                    } else {
                        for (let j = 0; j < phraseWords.length; j++) {
                            let fromValue: number = (j === 0) ? i : additionalStateIndex - 1;
                            let toValue: number;
                            if (j === phraseWords.length - 1) {
                                toValue = i + 1;
                            } else {
                                toValue = additionalStateIndex;
                                additionalStateIndex++;
                            }
                            transition = {
                                from: fromValue, to: toValue,
                                word: phraseWords[j].toUpperCase(), logp: 0.0, glue: notRequired
                            };
                            transitions = transitions.concat(
                                this.transitionAndFoils(transition, pronunciationCorrectness, dictionaryList));
                        }
                    }
                });

            }
        }

        this.grammar = {
            numStates: additionalStateIndex + 1,
            start: 0,
            end: transitions[transitions.length - 1].to,
            transitions: transitions
        };
    }

    private transitionAndFoils(transition: ITransition,
                                  pronunciationCorrectness: number, dictionaryList: string[]): ITransition[] {
        let transitions:ITransition[] = [transition];
        transitions = transitions.concat(this.addFoil(transition, pronunciationCorrectness, dictionaryList,
                                                      RecognizerGrammarService.FOIL_TAG));
        transitions = transitions.concat(this.addFoil(transition, pronunciationCorrectness, dictionaryList,
                                                      RecognizerGrammarService.START_TAG));
        return transitions;
    }

    // log to console to display the status
    private logEntry(entry): void {
        // don't log if running on iPad/device
        // @ts-ignore
      if (!window.cordova && window.console && window.console.log) {
            window.console.log(entry);
        }
    }

    private getTransitionsAfterIntervention(transitions: Array<ITransition>,
                                            wordId, isAudioIntervention): ITransition[] {
        let newTransitions: Array<ITransition> = [];
        let i = 0;
        let word = transitions[i].word;
        while ((i < transitions.length) && (word.split("-")[0] !== wordId)) {
            i++;
            word = transitions[i].word;
        }
        this.logEntry("grammar create on intervention: restarting from wordid " + wordId
                      + " index " + i + " len " + transitions.length);

        if (isAudioIntervention && i < transitions.length) {
            // after an audio intervention, intervened word is treated like a glue word - we don't require it to be said
            transitions[i].glue = true;
        }
        let transition_offset = transitions[i].from; // shift everything back so that the new start point is 0

        for (let j = i; j < transitions.length; j++) {
            // copy entries from i to end of sentence, but have to shift the from/to values
            newTransitions.push({
                                    from: transitions[j].from - transition_offset,
                                    to: transitions[j].to - transition_offset,
                                    logp: transitions[j].logp,
                                    word: transitions[j].word,
                                    glue: transitions[j].glue
                                });
        }

        return newTransitions;

    }

    private addFoil(transition: ITransition, pronunciationCorrectness: number,
                    dictionaryList: string[], tag: string): ITransition[] {
        // add the filler and silence skips
        let foilTransitions: ITransition[] = [];
        let logpFoil;
        let foilWord = transition.word + tag;
        // only add if: not a glue word, not lenient pronunciation correctness, and foil word is in dictionary
        if (!transition.glue && pronunciationCorrectness >= 25 && dictionaryList.indexOf(foilWord) >= 0) {
            logpFoil = this.getFoilProbability(pronunciationCorrectness, tag);
            foilTransitions.push({from: transition.from, to: transition.to, logp: logpFoil, word: foilWord});
        }

        return foilTransitions;

    }

    private addSkips(pronunciationCorrectness: number): void {
        // add the filler and silence skips
        let logpSilence;
        if (pronunciationCorrectness < 25) {
            return;
        }
        for (let i = 0; i < this.grammar.transitions.length; i++) {
            let fromState: number = this.grammar.transitions[i].from;
            let toState: number = this.grammar.transitions[i].to;
            // last word (or any transition to end) gets different probability, but only if not a single-word sentence
            let lastWordRule = (this.grammar.end > 1) && (toState === this.grammar.end);
            if (this.isWord(this.grammar.transitions[i].word)) {
                logpSilence = this.getSkipProbability(
                    pronunciationCorrectness, lastWordRule, this.grammar.transitions[i].glue);
                this.grammar.transitions.push({
                                                  from: fromState, to: toState, logp: logpSilence,
                                                  word: RecognizerGrammarService.SILENCE
                                              });
            }
        }
    }

    private addBackTransitions(pronunciationCorrectness: number): void {
        for (let i = 0; i < this.grammar.transitions.length; i++) {
            let fromState: number = this.grammar.transitions[i].to;
            let toState: number = this.grammar.transitions[i].from;
            if (this.isWord(this.grammar.transitions[i].word)) {
                this.grammar.transitions.push({
                                                  from: fromState,
                                                  to: toState,
                                                  logp: this.getBackProbability(pronunciationCorrectness),
                                                  word: RecognizerGrammarService.SILENCE
                                              });
            }
        }
    }

    private addResetTransitions(pronunciationCorrectness): void {
        for (let i = 0; i < this.grammar.transitions.length; i++) {
            let fromState:number = this.grammar.transitions[i].to;
            if (fromState !== 0 && this.isWord(this.grammar.transitions[i].word)) {
                this.grammar.transitions.push(
                    {from: fromState, to: 0, logp: this.getResetProbability(pronunciationCorrectness),
                        word: RecognizerGrammarService.SILENCE
                    });
            }
        }
    }

    private isWord(grammarString: string): boolean {
        return (!grammarString.endsWith(RecognizerGrammarService.FOIL_TAG) &&
                (grammarString !== RecognizerGrammarService.SILENCE) &&
                (grammarString !== RecognizerGrammarService.FILLER));
    }

    private addSelfTransitions(pronunciationCorrectness): void {

        let numStates = this.grammar.numStates;
        for (let i = 0; i < numStates; i++) {
            let fromState:number = i;
            this.grammar.transitions.push({
                                              from: fromState,
                                              to: fromState,
                                              logp: this.getSelfLoopProbability(pronunciationCorrectness, true),
                                              word: RecognizerGrammarService.SILENCE
                                          });
            if (this.probability.useFillerSelfLoop) {
                this.grammar.transitions.push({
                                                  from: fromState,
                                                  to: fromState,
                                                  logp: this.getSelfLoopProbability(pronunciationCorrectness, false),
                                                  word: RecognizerGrammarService.FILLER
                                              });
            }
        }
    }

    private getSelfLoopProbability(pronunciationCorrectness, isSilence): number {
        if (isSilence) {
            return this.probability.silenceSelfLoop; // base value
        }
        return this.probability.fillerSelfLoop;
    }

    private getSkipProbability(pronunciationCorrectness, isLastWord, isGlue): number {

        if (isLastWord) {
            return this.calculateProbability(this.probability.lastWordSkip, pronunciationCorrectness);
        } else if (isGlue) {
            return this.calculateProbability(this.probability.glueSkip, pronunciationCorrectness);
        }
        return this.calculateProbability(this.probability.silenceSkip, pronunciationCorrectness);
    }

    private getFoilProbability(pronunciationCorrectness: number, tag: string): number {
        if (tag === RecognizerGrammarService.FOIL_TAG) {
            return this.calculateProbability(this.probability.foilSkip, pronunciationCorrectness);
        } else {
            return this.calculateProbability(this.probability.startSkip, pronunciationCorrectness);
        }
    }

    private getBackProbability(pronunciationCorrectness: number): number {
        return this.probability.silenceBack;
    }

    private getResetProbability(pronunciationCorrectness: number): number {
        return this.probability.reset;
    }

    private calculateProbability(sliderSettings: ISliderSettings, pronunciationCorrectness: number): number {
        // we are either interpolating value between slider setting 0 and 50, or 50 and 100
        let scale: number; // value between 0 and 1; how along we are between 0 and 50 or 50 and 100
        let start: number; // initial probability value
        let maxIncrement: number; // maximum change in probability value in the range (0 to 50, or 50 to 100)
        if (pronunciationCorrectness < 50) {
            scale = pronunciationCorrectness / 50.0;
            start = sliderSettings.pronunciationCorrectness0;
            maxIncrement = sliderSettings.pronunciationCorrectness50 - sliderSettings.pronunciationCorrectness0;
        } else {
            scale = (pronunciationCorrectness - 50) / 50.0;
            start = sliderSettings.pronunciationCorrectness50;
            maxIncrement = sliderSettings.pronunciationCorrectness100 - sliderSettings.pronunciationCorrectness50;
        }
        return (start + scale * maxIncrement);
    }

}
